import { Phone, Mail } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { PHONES, EMAIL } from "@/lib/site";

const FAQS = [
  {
    q: "What services does ArcView Homes provide?",
    a: "ArcView Homes provides residential design, drafting, assistance with permissions and approvals, custom building and end-to-end project handling.",
  },
  {
    q: "Do you design custom homes?",
    a: "Yes. ArcView Homes provides unique plans and designs tailored to individual client requirements.",
  },
  {
    q: "Can you build on an irregular block?",
    a: "ArcView Homes specialises in creating custom home solutions for clients who own irregular or uniquely shaped blocks of land.",
  },
  { q: "Do you have an architect?", a: "Yes. ArcView Homes has an in-house architect." },
  {
    q: "Do you provide construction as well as design?",
    a: "Yes. ArcView Homes provides an integrated design and building service through its team and custom builders.",
  },
  {
    q: "How long does the process take?",
    a: "The stated handover timeframe is up to 6 months, subject to the project scope, approvals, site conditions and other project requirements.",
  },
  {
    q: "Where is ArcView Homes located?",
    a: "ArcView Homes is based in Tarneit, Victoria 3029.",
  },
  {
    q: "How can I get started?",
    a: "Contact ArcView Homes using the enquiry form or call one of the listed phone numbers.",
  },
];

export default function Faq() {
  return (
    <section id="faqs" className="bg-cream py-24 sm:py-32" data-testid="faq-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[1fr_1.4fr] gap-14">
        <div>
          <Reveal>
            <p className="eyebrow text-arc mb-6">FAQs</p>
          </Reveal>
          <MaskedLines
            className="font-serif font-medium tracking-tight leading-[1.05] text-charcoal text-4xl sm:text-5xl"
            lines={[<>Frequently Asked</>, <><span className="italic text-gradient">Questions.</span></>]}
          />
          <Reveal delay={0.2}>
            <p className="mt-6 text-mist leading-relaxed max-w-sm">
              Can't find your answer? Call us or send an enquiry — we're happy to talk through your
              project.
            </p>
            <div className="mt-7 space-y-3">
              {PHONES.map((p) => (
                <a
                  key={p.tel}
                  href={`tel:${p.tel}`}
                  data-testid={`faq-phone-${p.tel}`}
                  className="flex items-center gap-3 text-charcoal font-semibold hover:text-arc transition-colors"
                >
                  <Phone size={16} className="text-arc" /> {p.display}
                </a>
              ))}
              <a
                href={`mailto:${EMAIL}`}
                data-testid="faq-email"
                className="flex items-center gap-3 text-charcoal font-semibold hover:text-arc transition-colors"
              >
                <Mail size={16} className="text-arc" /> {EMAIL}
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="border-t border-line" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-line">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i + 1}`}
                  className="font-serif text-lg sm:text-xl text-charcoal py-6 hover:text-arc hover:no-underline text-left"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-mist leading-relaxed text-sm sm:text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}
